import { inject, observer } from 'mobx-react';
import * as React from 'react';
import { PhotoViewerConstants } from '../../Config/UIConstants';
import { AudioComment, Comment, TextComment } from '../../Models/Comment';
import {
  warnBeforeDelete,
  IAcceptsDeferredLeaveAction
} from '../../SharedComponents/HigherOrderComponents/WarnsBeforeDelete';
import { StoryPhotoCommentsStore } from '../../Stores/StoryPhotoCommentsStore';
import { ApplicationStore } from '../../_stores/ApplicationStore';
import * as images from '../../images/images';
import { colors } from '../../styles/colors';
import { ExistingCommentAudioPlayer } from './Audio/ExistingCommentAudioPlayer';
import { CommentInput } from './CommentInput';
import { CommentType } from './PhotoViewer';

interface IProps {
    comment: Comment;
    index: number;
    store?: ApplicationStore;
    margin?: number;
}

interface IState {
    repliesVisible: boolean;
    activeCommentType: CommentType;
}

@inject('store')
@observer
class CommentTreeRowClass extends React.Component<IProps & IAcceptsDeferredLeaveAction, IState> {
    private isExpectingReplyPanelToOpen = false;

    public state: IState = {
        repliesVisible: true,
        activeCommentType: CommentType.WRITE,
    };

    private get store(): StoryPhotoCommentsStore {
        return this.props.store!.storyPhotoCommentStore;
    }

    public componentDidUpdate(prevProps: Readonly<IProps & IAcceptsDeferredLeaveAction>, prevState: Readonly<IState>, prevContext: any): void {
        if (this.isExpectingReplyPanelToOpen && this.replyInputRef !== null) {
            this.isExpectingReplyPanelToOpen = false;
            this.replyInputRef.focus();
        }
    }

    private replyInputRef: any;

    private handleAddComment = async () => {
        await this.store.postComment(this.props.comment.storyID, this.props.comment.photoID, this.props.comment.replyParentId);
        this.setState({ activeCommentType: CommentType.WRITE });
    }

    private changeTypeOrWarnIfDirty = (type: CommentType): void => {
        this.props.onTryNavigate(this.store.replyEditState!.hasUnsubmittedData, () => {
            this.store.replaceReplyEditState();
            this.setState({
                activeCommentType: type,
            }, () => {
                if (type === CommentType.WRITE && this.replyInputRef !== null) {
                    this.replyInputRef.focus();
                }
            });
        });
    }

    private onClickReply = () => {
        this.props.onTryNavigate(this.store.hasUnsubmittedData, () => {
            const isClosing = this.store.replyTargetId === this.props.comment.id;
            if (isClosing) {
                this.store.setReplyTargetId(undefined);
            } else {
                this.store.setReplyTargetId(this.props.comment.id);
            }
        });
    }

    private renderCommentBody(comment: Comment) {
        if (comment instanceof TextComment) {
            return <p style={styles.commentText}>{`${comment.text}`}</p>;
        } else if (comment instanceof AudioComment) {
            return (
                <ExistingCommentAudioPlayer
                    startWithUri={comment.audioURL}
                    startWithUriLength={comment.audioLengthMS}
                />
            );
        } else {
            console.info(`Tried to render a comment with no text and no audio (${comment.id})`);
            return null;
        }
    }

    public render() {
        const { comment, index, margin } = this.props;
        //removing 10 seconds because comment appears in the future 'in a few seconds' rather than 'a few seconds ago' on comment submission;
        const timestamp = comment.createdAt
            .clone()
            .subtract(10, 'seconds')
            .fromNow();

        let isReplyAreaVisible = this.store.replyTargetId === this.props.comment.id;

        return (
            <div key={comment.id} style={{ ...(index % 2 !== 0 ? styles.oddComment : styles.evenComment), marginLeft: margin || 0 }}>
                <div style={styles.commentContainer}>
                    <div style={styles.nameContainer}>
                        {comment.author.profile_image_url ? (
                            <img src={comment.author.profile_image_url || images.NoProfileImageUrl} style={styles.namePlateImage} />
                        ) : (
                            <div style={styles.namePlateImage}>
                                <p style={styles.initialsText}>{comment.author.first_name[0] + comment.author.last_name[0]}</p>
                            </div>
                        )}
                        <p style={styles.nameText}>{`${comment.author.first_name} ${comment.author.last_name}`}</p>
                    </div>
                    {this.renderCommentBody(comment)}
                    <div style={styles.replyContainer}>
                        <p style={styles.replyButtonText}>
                            {comment.children.length > 0 && [
                                <span style={styles.button} key="replies" onClick={() => this.setState({ repliesVisible: !this.state.repliesVisible })}>
                                    {this.state.repliesVisible ? PhotoViewerConstants.HIDE_REPLIES : PhotoViewerConstants.NUMBER_OF_REPLIES(comment.children.length)}
                                </span>,
                                <span key="dash"> - </span>
                            ]}
                            <span
                                onClick={this.onClickReply}
                                style={styles.button}
                            >
                                {PhotoViewerConstants.REPLY}
                            </span>
                        </p>
                        <p style={styles.timeStampText}>{timestamp} </p>
                    </div>
                </div>
                {isReplyAreaVisible && (
                    <div style={styles.replyArea(margin)}>
                        <div style={styles.commentTypeTabs}>
                            <div style={styles.commentTypeContainer} onClick={() => this.changeTypeOrWarnIfDirty(CommentType.WRITE)}>
                                <p style={styles.commentTabText}>{PhotoViewerConstants.WRITE}</p>
                                {this.state.activeCommentType === CommentType.WRITE && <div style={styles.selectedBar} />}
                            </div>

                            <div style={styles.commentTypeContainer} onClick={() => this.changeTypeOrWarnIfDirty(CommentType.RECORD)}>
                                <p style={styles.commentTabText}>{PhotoViewerConstants.RECORD}</p>
                                {this.state.activeCommentType === CommentType.RECORD && <div style={styles.selectedBar} />}
                            </div>
                        </div>
                        <div style={styles.commentInput}>
                            <CommentInput
                                setReference={ref => (this.replyInputRef = ref)}
                                activeCommentType={this.state.activeCommentType}
                                onAddComment={this.handleAddComment}
                                commentEditState={this.store.replyEditState!}
                            />
                        </div>
                    </div>
                )}
                {this.state.repliesVisible &&
                        [...comment.children].map((reply, idx) => {
                    return <CommentTreeRow key={reply.id} comment={reply} index={idx} margin={30}
                    />;
                })}
            </div>
        );
    }
}

export const CommentTreeRow = warnBeforeDelete(CommentTreeRowClass);

const styles = {
    replyArea: (margin: number | undefined) => {
        return {
            paddingRight: 20,
            paddingLeft: 25
        };
    },
    initialsText: { color: colors.WHITE, fontSize: 12, fontWeight: 'bold' } as React.CSSProperties,
    inputText: { fontSize: 13, paddingBottom: 0, color: colors.LIGHT_GREY },
    sideButtonText: {
        fontSize: 12
    },
    sideButton: {
        height: 32,
        width: 60
    } as React.CSSProperties,
    inputPadding: {
        paddingTop: 8,
        display: 'flex',
        flexDirection: 'row'
    } as React.CSSProperties,
    inputContainer: {
        display: 'flex',
        flex: 1,
        backgroundColor: 'rgba(255, 255, 255, 0.12)',
        height: 32
    } as React.CSSProperties,
    commentInput: {
        width: '100%',
        paddingBottom: 20
    },
    selectedBar: {
        width: '100%',
        height: 2,
        backgroundColor: colors.ACTION_GREEN
    },
    commentTabText: {
        width: 60,
        textAlign: 'center',
        fontSize: 12,
        color: colors.ACTION_GREEN
    } as React.CSSProperties,
    commentTypeContainer: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        cursor: 'pointer'
    } as React.CSSProperties,
    commentTypeTabs: {
        height: 28,
        display: 'flex',
        flexDirection: 'row',
        borderBottomColor: 'rgba(255, 255, 255, 0.12)',
        borderBottomWidth: 1,
        borderBottomStyle: 'solid'
    } as React.CSSProperties,
    commentContainer: {
        padding: 25,
        display: 'flex',
        alignSelf: 'stretch',
        flexDirection: 'column'
    } as React.CSSProperties,
    evenComment: {
        backgroundColor: '#61606b',
        display: 'flex',
        alignSelf: 'stretch',
        flexDirection: 'column'
    } as React.CSSProperties,
    oddComment: {
        backgroundColor: '#6c6b75',
        display: 'flex',
        alignSelf: 'stretch',
        flexDirection: 'column'
    } as React.CSSProperties,
    commentText: {
        color: 'white',
        marginTop: 17,
        fontSize: 16,
        fontWeight: 'lighter',
        opacity: 0.9,
        whiteSpace: 'pre-wrap'
    } as React.CSSProperties,
    nameContainer: {
        display: 'flex',
        alignItems: 'center'
    } as React.CSSProperties,
    namePlateImage: {
        height: 30,
        width: 30,
        objectFit: 'contain',
        marginRight: 10,
        borderRadius: 15,
        background: 'linear-gradient(180deg, #8E9195 0%, #65676B 100%)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
    } as React.CSSProperties,
    nameText: {
        color: 'white',
        fontSize: 16,
        fontWeight: 'bolder'
    } as React.CSSProperties,
    replyContainer: {
        display: 'flex',
        justifyContent: 'space-between',
        marginTop: 8
    } as React.CSSProperties,
    replyButtonText: {
        color: '#ff8040'
    },
    timeStampText: {
        color: '#FFFFFF80',
        fontSize: 14
    },
    button: {
        cursor: 'pointer'
    }
};



// WEBPACK FOOTER //
// ./src/Pages/PhotoViewer/CommentTreeRow.tsx